/**
 * Names for inbox message rows (sender, conversation, Space), resolved from
 * the providers that already hold them, plus the notification detail settings.
 * Safe outside the conversations / Spaces providers: lookups return undefined.
 */

import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InboxItem } from '@adieuu/shared';
import { ConversationsContext } from './conversations/context';
import { SpacesContext } from './spaces/context';
import { useCipherStore } from './useCipherStore';
import { useNotificationDisplaySettings } from './useNotificationDisplaySettings';
import { useNotificationSenderNames } from './useNotificationSenderNames';
import { getSpaceCipherLink } from '../services/spaceCipherService';
import { resolveSpaceDisplayName } from '../pages/spaces/spaceMetadataCipher';
import type { InboxNotificationLookups } from '../app/sidebar/inboxItemDescriptor';

const MESSAGE_ROW_TYPES = new Set([
  'conversation_message',
  'conversation_message_reply',
  'message_reaction',
  'space_message_mention',
  'space_message_reply',
]);

const SPACE_ROW_TYPES = new Set(['space_message_mention', 'space_message_reply']);

/** Senders whose names the visible rows would show. */
function senderIdsToResolve(
  items: readonly InboxItem[],
  showConversationSender: boolean,
  showSpaceSender: boolean,
): string[] {
  const ids: string[] = [];
  for (const item of items) {
    if (item.kind === 'space_mention') {
      if (showSpaceSender) ids.push(item.fromIdentityId);
      continue;
    }
    if (!MESSAGE_ROW_TYPES.has(item.type)) continue;
    const isSpace = SPACE_ROW_TYPES.has(item.type);
    if (isSpace ? !showSpaceSender : !showConversationSender) continue;
    const from = item.data.fromIdentityId;
    if (typeof from === 'string') ids.push(from);
  }
  return ids;
}

export function useInboxNotificationLookups(items: readonly InboxItem[]): InboxNotificationLookups {
  const { t } = useTranslation();
  const { display } = useNotificationDisplaySettings();
  const conversations = useContext(ConversationsContext)?.conversations;
  const spaces = useContext(SpacesContext)?.spaces;
  const { getCipherKey } = useCipherStore();

  const senderIds = useMemo(
    () => senderIdsToResolve(items, display.conversations.showSender, display.spaces.showSender),
    [items, display.conversations.showSender, display.spaces.showSender],
  );
  const senderNames = useNotificationSenderNames(senderIds);

  const conversationFor = useCallback(
    (conversationId: string) => {
      const conv = conversations?.find((c) => c.id === conversationId);
      return conv ? { type: conv.type, name: conv.decryptedName } : undefined;
    },
    [conversations],
  );

  const spaceNameFor = useCallback(
    (spaceId: string) => {
      const space = spaces?.find((s) => s.id === spaceId);
      if (!space) return undefined;
      const cipherId = getSpaceCipherLink(space.id);
      return resolveSpaceDisplayName(space, cipherId ? getCipherKey(cipherId) : null, {
        encryptedSpace: t('spaces.encryptedSpacePlaceholder'),
      });
    },
    [spaces, getCipherKey, t],
  );

  const senderNameFor = useCallback((identityId: string) => senderNames[identityId], [senderNames]);

  return useMemo(
    () => ({ display, conversationFor, spaceNameFor, senderNameFor }),
    [display, conversationFor, spaceNameFor, senderNameFor],
  );
}
